/**
 * runtime.ts — THE FEED homepage's shared motion engine (design.md Part III).
 * One module owns every observer and the page's ONLY scroll listener, so the
 * sections never fight over scroll/resize work:
 *
 *  - reveals:   `[data-fx]`, `[data-captions]`, `.count[data-count]` get `.in`
 *               once, when they enter (one IntersectionObserver, threshold 0 —
 *               fractional thresholds never fire on elements taller than the
 *               viewport);
 *  - ambient:   `[data-live]` gets `.live` while near the viewport (both ways);
 *  - stagger:   children of `[data-stagger]` carrying `data-i` get `--i` (CSSOM —
 *               the CSP forbids inline style attributes, not setProperty);
 *  - captions:  word-by-word pop + the karaoke pill that parks on the key word;
 *  - counters:  odometer strips (never for money);
 *  - scenes:    `registerScene()` — the scroll driver writes `--p` (0→1) for the
 *               three scroll-linked moments, rAF-batched, only when it changes;
 *  - story bar: chapter progress (`--seg-p`) + aria-current;
 *  - media:     `video[data-reel]` load near the viewport and autoplay muted
 *               within a decode budget (2 on phones / ≤4 cores, else 3); none
 *               under reduced motion or Save-Data.
 *
 * Static-first: everything here only ADDS motion to a page that is already
 * complete; under reduced motion the reveal classes are applied at once.
 */

export const reduced = (): boolean =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const root = document.documentElement;
const motionOn = (): boolean => root.classList.contains("js-motion");

/* ── reveals ─────────────────────────────────────────────────────────────── */

type RevealHook = (el: Element) => void;
const revealHooks: RevealHook[] = [];
/** Run `hook(el)` the moment any revealed element gets `.in`. */
export function onReveal(hook: RevealHook): void {
  revealHooks.push(hook);
}

function reveal(el: Element): void {
  if (el.classList.contains("in")) return;
  el.classList.add("in");
  for (const hook of revealHooks) hook(el);
}

function initStagger(): void {
  for (const parent of document.querySelectorAll<HTMLElement>(
    "[data-stagger]",
  )) {
    const kids = parent.querySelectorAll<HTMLElement>("[data-i]");
    // keep the whole cascade under ~900ms however many children there are
    const step = Math.min(60, Math.floor(900 / Math.max(1, kids.length)));
    parent.style.setProperty("--stagger", `${step}ms`);
    for (const kid of kids) {
      kid.style.setProperty("--i", kid.getAttribute("data-i") ?? "0");
    }
  }
}

function initReveals(): void {
  const targets = document.querySelectorAll(
    ".page-home [data-fx], .page-home [data-captions], .page-home .count[data-count]",
  );
  if (!motionOn() || !("IntersectionObserver" in window)) {
    for (const el of targets) reveal(el);
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        reveal(entry.target);
        io.unobserve(entry.target);
      }
    },
    { threshold: 0, rootMargin: "0px 0px -12% 0px" },
  );
  for (const el of targets) io.observe(el);
}

function initLive(): void {
  const targets = document.querySelectorAll("[data-live]");
  if (!("IntersectionObserver" in window)) return;
  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        entry.target.classList.toggle(
          "live",
          entry.isIntersecting && motionOn(),
        );
      }
    },
    { threshold: 0, rootMargin: "10% 0px" },
  );
  for (const el of targets) io.observe(el);
}

/* ── captions ────────────────────────────────────────────────────────────── */

function initCaptions(): void {
  for (const head of document.querySelectorAll<HTMLElement>(
    "[data-captions]",
  )) {
    const words = Array.from(head.querySelectorAll<HTMLElement>(".w"));
    const step = Math.min(55, Math.floor(900 / Math.max(1, words.length)));
    head.style.setProperty("--cap-stagger", `${step}ms`);
    words.forEach((w, i) => w.style.setProperty("--wi", String(i)));
  }
  onReveal((el) => {
    if (!(el instanceof HTMLElement) || !el.hasAttribute("data-captions"))
      return;
    if (!motionOn()) return;
    runPill(el);
  });
}

/** The karaoke pill: steps from word to word in time with the pop, then parks on
 * the key word (`data-key`); on a red key word it parks as an underline bar. */
function runPill(head: HTMLElement): void {
  const words = Array.from(head.querySelectorAll<HTMLElement>(".w"));
  if (words.length === 0) return;
  const pill = document.createElement("span");
  pill.className = "cap-pill";
  pill.setAttribute("aria-hidden", "true");
  head.prepend(pill);
  const step = parseFloat(head.style.getPropertyValue("--cap-stagger")) || 55;
  const BASE_W = 100;
  // The pill's own untransformed box is the origin: it sits at the heading's
  // inline-start (the RIGHT edge on /ar), and inside a zoomed .wrap (≥2000px,
  // --ui-zoom) its CSS px are larger than screen px — its measured width
  // against its 100px CSS width gives that ratio. Word rects are screen px.
  pill.style.transform = "none";
  const origin0 = pill.getBoundingClientRect();
  const z0 = origin0.width > 0 ? origin0.width / BASE_W : 1;
  // The origin is re-derived on every place(), in the same frame as the word
  // rect: the page scrolls (and the heading may reflow or resize) between the
  // reveal and the later settles, so a rect captured once goes stale. The
  // pill's live rect minus the translation it is CURRENTLY rendered at (the
  // computed matrix, which also covers a mid-transition value) is its
  // untransformed top-left; transform-origin is 0 0, so scaleX moves neither.
  // The zoom ratio is re-read too (a resize can cross a --ui-zoom step): the
  // rendered width is BASE_W × scaleX × zoom.
  const originNow = (): { left: number; top: number; z: number } => {
    const pr = pill.getBoundingClientRect();
    const tf = getComputedStyle(pill).transform;
    const m = tf && tf !== "none" ? new DOMMatrixReadOnly(tf) : null;
    const sx = m ? m.a : 1;
    const z = pr.width > 0 && sx > 0 ? pr.width / (BASE_W * sx) : z0;
    return {
      left: pr.left - (m ? m.e : 0) * z,
      top: pr.top - (m ? m.f : 0) * z,
      z,
    };
  };
  const keyIndex = Math.max(
    0,
    words.findIndex((w) => w.hasAttribute("data-key")),
  );
  const hasKey = words.some((w) => w.hasAttribute("data-key"));
  // A parked pill spans every key word that shares the first key's line (e.g.
  // the red "CONTENT NEVER" pair in #never); keys wrapped onto another line
  // keep their own styling and the pill stays on the first.
  const keys = words.filter((w) => w.hasAttribute("data-key"));
  const spanRect = (first: HTMLElement): DOMRect => {
    const f = first.getBoundingClientRect();
    let left = f.left;
    let right = f.right;
    for (const k of keys) {
      if (k === first) continue;
      const r = k.getBoundingClientRect();
      if (Math.abs(r.top - f.top) > f.height * 0.4) continue;
      left = Math.min(left, r.left);
      right = Math.max(right, r.right);
    }
    return new DOMRect(left, f.top, right - left, f.height);
  };
  const place = (w: HTMLElement, asBar: boolean, span = false): void => {
    const r = span ? spanRect(w) : w.getBoundingClientRect();
    const origin = originNow();
    const z = origin.z;
    const padX = 0.12 * r.height;
    const x = (r.left - origin.left - padX) / z;
    const y = asBar
      ? (r.bottom - origin.top) / z - 2
      : (r.top - origin.top + r.height * 0.06) / z;
    const h = asBar ? 4 : (r.height * 0.9) / z;
    pill.classList.toggle("is-bar", asBar);
    pill.style.setProperty("block-size", `${h}px`);
    pill.style.transform = `translate(${x}px, ${y}px) scaleX(${(r.width + padX * 2) / z / BASE_W})`;
  };
  const last = hasKey ? keyIndex : words.length - 1;
  let i = 0;
  const tickStep = Math.max(step, 70);
  const tick = (): void => {
    const w = words[i];
    if (!w) return;
    place(w, false);
    pill.classList.add("is-on");
    if (i >= last) {
      const key = words[last];
      if (!key) return;
      const red = key.classList.contains("is-red");
      if (hasKey) {
        place(key, red, true);
        for (const k of keys) k.classList.add("is-key-on");
        // words are still settling from their pop (scale .9 + offset) when the
        // pill parks: measure again once they rest, and on any later reflow
        const settle = (): void => place(key, red, true);
        window.setTimeout(settle, 420);
        if ("ResizeObserver" in window)
          new ResizeObserver(settle).observe(head);
        // scroll-driven scenes can rescale a parked word (e.g. #never): measure
        // once more when a scroll gesture ends
        window.addEventListener("scrollend", settle, { passive: true });
      } else {
        pill.classList.remove("is-on");
      }
      return;
    }
    i += 1;
    window.setTimeout(tick, tickStep);
  };
  window.setTimeout(tick, 80);
}

/* ── counters (odometer) ─────────────────────────────────────────────────── */

function initCounters(): void {
  if (!motionOn()) return;
  for (const count of document.querySelectorAll<HTMLElement>(
    ".count[data-count]",
  )) {
    const odo = count.querySelector<HTMLElement>(".odo");
    const text = count.getAttribute("data-count") ?? "";
    if (!odo || !text) continue;
    let di = 0;
    for (const ch of text) {
      if (/[0-9]/.test(ch)) {
        const d = document.createElement("span");
        d.className = "odo__d";
        const strip = document.createElement("span");
        strip.className = "odo__strip";
        for (let n = 0; n <= 9; n += 1) {
          const s = document.createElement("span");
          s.textContent = String(n);
          strip.append(s);
        }
        strip.style.setProperty("--d", ch);
        strip.style.setProperty("--di", String(di));
        di += 1;
        d.append(strip);
        odo.append(d);
      } else {
        const s = document.createElement("span");
        s.textContent = ch;
        odo.append(s);
      }
    }
    count.classList.add("is-built");
  }
}

/* ── the scroll driver ───────────────────────────────────────────────────── */

export type SceneMode =
  /** 0 at page top → 1 when scrolled by the element's own height (the cover) */
  | "cover"
  /** 0 when the element's top meets the viewport bottom → 1 when its bottom
   * leaves the viewport top */
  | "pass"
  /** 0 when the element's top reaches the viewport top → 1 when its bottom
   * reaches the viewport bottom (a pinned/sticky scene) */
  | "pin";

interface Scene {
  el: HTMLElement;
  mode: SceneMode;
  minWidth: number;
  top: number;
  height: number;
  last: number;
  onProgress?: (p: number) => void;
}
const scenes: Scene[] = [];
let vh = window.innerHeight;
let docH = 0;
let pending = false;
const frameHooks: Array<(y: number) => void> = [];

/** A scroll-linked moment: the driver writes `--p` on `el` (3 decimals, only
 * when it changes) and calls `onProgress(p)`. Not written under reduced motion
 * or below `minWidth`. */
export function registerScene(
  el: HTMLElement,
  mode: SceneMode,
  opts: { minWidth?: number; onProgress?: (p: number) => void } = {},
): void {
  scenes.push({
    el,
    mode,
    minWidth: opts.minWidth ?? 0,
    top: 0,
    height: 0,
    last: -1,
    onProgress: opts.onProgress,
  });
  measure();
}

/** Run `hook(scrollY)` in the driver's frame (read-only work, then writes). */
export function onFrame(hook: (y: number) => void): void {
  frameHooks.push(hook);
}

function measure(): void {
  const probe = document.querySelector<HTMLElement>(".vh-probe");
  vh = probe ? probe.getBoundingClientRect().height : window.innerHeight;
  docH = document.documentElement.scrollHeight;
  const y = window.scrollY;
  for (const s of scenes) {
    const r = s.el.getBoundingClientRect();
    s.top = r.top + y;
    s.height = r.height;
    s.last = -1;
  }
  measureChapters();
  schedule();
}

function progress(s: Scene, y: number): number {
  let p: number;
  if (s.mode === "cover") p = y / Math.max(1, s.height);
  else if (s.mode === "pass") p = (y + vh - s.top) / Math.max(1, vh + s.height);
  else p = (y - s.top) / Math.max(1, s.height - vh);
  return Math.min(1, Math.max(0, p));
}

let lastScroll = "";
let navEl: HTMLElement | null = null;

function frame(): void {
  pending = false;
  const y = window.scrollY;
  const w = window.innerWidth;
  // --scroll feeds only the nav hairline (chrome.css .site-nav::after, which
  // saturates at 2.5% of the page). Writing it on <html> every frame forced a
  // whole-document style recalc while scrolling, so it is set on the header
  // alone, clamped where the hairline saturates, and only when it changes.
  const sc = Math.min(0.05, y / Math.max(1, docH - vh)).toFixed(3);
  if (sc !== lastScroll) {
    lastScroll = sc;
    navEl ??= document.querySelector<HTMLElement>("[data-nav]");
    navEl?.style.setProperty("--scroll", sc);
  }
  if (!reduced()) {
    for (const s of scenes) {
      if (w < s.minWidth) continue;
      // skip scenes more than a viewport away
      if (y + vh * 2 < s.top || y - vh > s.top + s.height) continue;
      const p = Math.round(progress(s, y) * 1000) / 1000;
      if (p === s.last) continue;
      s.last = p;
      s.el.style.setProperty("--p", String(p));
      s.onProgress?.(p);
    }
  }
  updateChapters(y);
  for (const hook of frameHooks) hook(y);
}

function schedule(): void {
  if (pending) return;
  pending = true;
  window.requestAnimationFrame(frame);
}

function initDriver(): void {
  window.addEventListener("scroll", schedule, { passive: true });
  let resizeTimer = 0;
  const later = (): void => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(measure, 150);
  };
  window.addEventListener("resize", later, { passive: true });
  window.addEventListener("orientationchange", later, { passive: true });
  if ("ResizeObserver" in window)
    new ResizeObserver(later).observe(document.body);
  void document.fonts?.ready.then(measure);
  measure();
}

/* ── story bar ───────────────────────────────────────────────────────────── */

interface Chapter {
  seg: HTMLElement;
  section: HTMLElement | null;
  start: number;
  end: number;
  last: number;
}
const chapters: Chapter[] = [];
let currentChapter = -1;

function initStory(): void {
  const segs = document.querySelectorAll<HTMLAnchorElement>(".story-seg");
  segs.forEach((seg) => {
    const id = (seg.getAttribute("href") ?? "").replace(/^.*#/, "");
    chapters.push({
      seg,
      section: id ? document.getElementById(id) : null,
      start: 0,
      end: 0,
      last: -1,
    });
    seg.addEventListener("click", (event) => {
      const target = id ? document.getElementById(id) : null;
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: reduced() ? "auto" : "smooth" });
      history.replaceState(null, "", `#${id}`);
    });
  });
}

function measureChapters(): void {
  const y = window.scrollY;
  chapters.forEach((ch, i) => {
    if (!ch.section) return;
    ch.start = ch.section.getBoundingClientRect().top + y - vh * 0.35;
    const next = chapters[i + 1]?.section;
    ch.end = next
      ? next.getBoundingClientRect().top + y - vh * 0.35
      : docH - vh;
    ch.last = -1;
  });
}

function updateChapters(y: number): void {
  if (chapters.length === 0) return;
  let active = -1;
  chapters.forEach((ch, i) => {
    const p =
      Math.round(
        Math.min(
          1,
          Math.max(0, (y - ch.start) / Math.max(1, ch.end - ch.start)),
        ) * 1000,
      ) / 1000;
    if (p !== ch.last) {
      ch.last = p;
      ch.seg.style.setProperty("--seg-p", String(p));
    }
    if (y >= ch.start) active = i;
  });
  if (active !== currentChapter) {
    chapters[currentChapter]?.seg.removeAttribute("aria-current");
    chapters[active]?.seg.setAttribute("aria-current", "step");
    currentChapter = active;
  }
  root.classList.toggle("story-on", y > 80);
}

/* ── media (video[data-reel]) ────────────────────────────────────────────── */

const playing = new Set<HTMLVideoElement>();
export const decodeBudget = (): number =>
  window.matchMedia("(pointer: coarse)").matches ||
  (navigator.hardwareConcurrency ?? 8) <= 4
    ? 2
    : 3;
const saveData = (): boolean =>
  Boolean(
    (navigator as Navigator & { connection?: { saveData?: boolean } })
      .connection?.saveData,
  );
/** Autoplay is allowed (motion on, no Save-Data). */
export const canAutoplay = (): boolean => !reduced() && !saveData();

/** Give the video its source (from data-src) if it has none yet. */
export function ensureSrc(v: HTMLVideoElement): void {
  if (v.getAttribute("src")) return;
  const src = v.getAttribute("data-src");
  if (src) {
    v.setAttribute("src", src);
    v.load();
  }
}

/** Play muted within the decode budget; returns false if over budget. */
export function playReel(v: HTMLVideoElement): boolean {
  if (!canAutoplay()) return false;
  if (!playing.has(v) && playing.size >= decodeBudget()) {
    // evict the oldest that is not in view
    const victim = playing.values().next().value as
      HTMLVideoElement | undefined;
    if (victim) pauseReel(victim);
  }
  ensureSrc(v);
  v.muted = true;
  playing.add(v);
  void v.play().catch(() => playing.delete(v));
  return true;
}

export function pauseReel(v: HTMLVideoElement): void {
  playing.delete(v);
  v.pause();
}

function initMedia(): void {
  const videos = document.querySelectorAll<HTMLVideoElement>(
    ".page-home video[data-reel]",
  );
  if (videos.length === 0 || !("IntersectionObserver" in window)) return;
  const near = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        ensureSrc(entry.target as HTMLVideoElement);
        near.unobserve(entry.target);
      }
    },
    { rootMargin: "600px 0px" },
  );
  const seen = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const v = entry.target as HTMLVideoElement;
        if (entry.isIntersecting) playReel(v);
        else pauseReel(v);
      }
    },
    { threshold: 0.35 },
  );
  for (const v of videos) {
    near.observe(v);
    // sections that run their own playlist mark videos data-reel="manual"
    if (v.getAttribute("data-reel") !== "manual") seen.observe(v);
  }
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) for (const v of Array.from(playing)) v.pause();
    else for (const v of Array.from(playing)) void v.play().catch(() => {});
  });
}

/* ── boot ────────────────────────────────────────────────────────────────── */

type SectionInit = () => void;

export function initHome(sections: SectionInit[]): void {
  if (!document.body.classList.contains("page-home")) return;
  initStagger();
  initCaptions();
  initCounters();
  initStory();
  // sections register their scenes / hooks before the first measure + reveal
  for (const init of sections) {
    try {
      init();
    } catch (err) {
      // one broken section must never take the page's motion down with it
      console.error(err);
    }
  }
  initReveals();
  initLive();
  initDriver();
  initMedia();
}
